import axios from "axios";

const RAW_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${RAW_BASE}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// ---------------------------------------------------------------------------
// Prerender hydration cache.
//
// The build-time Puppeteer prerender (`frontend/scripts/prerender.js`)
// injects a `<script>window.__PRERENDER_CACHE__ = {…}</script>` block into
// each snapshot. It maps the axios request path (path portion only, e.g.
// `/content/provider/yessenia-quirola`) → the JSON response body captured
// during prerender.
//
// This interceptor short-circuits the first GET on each of those paths and
// returns the cached body immediately. Two effects:
//   1. React hydrates with the exact same data the prerender saw, so
//      `<h1>` / bio / photo don't flash to "Loading" or "Page not found"
//      while the network request races.
//   2. We avoid an extra API round-trip on the very first paint.
//
// After the cache is drained the interceptor becomes a no-op and normal
// live requests take over — no long-term staleness risk.
// ---------------------------------------------------------------------------
api.interceptors.request.use((config) => {
  if (typeof window === "undefined") return config;
  const cache = window.__PRERENDER_CACHE__;
  if (!cache) return config;
  const method = (config.method || "get").toLowerCase();
  if (method !== "get") return config;
  // Normalise the request key: axios treats config.url as an absolute or
  // baseURL-relative path. We want the "path" portion only (no host).
  let key = config.url || "";
  try {
    const u = new URL(key, "http://x.local");
    key = u.pathname + (u.search || "");
  } catch (_) {
    // fall through — treat as-is
  }
  // Strip an accidental /api prefix so callers can pre-populate either
  // `/api/content/…` or `/content/…`.
  const normalized = key.replace(/^\/api/, "");
  const hit = cache[normalized] ?? cache[key] ?? cache["/api" + normalized];
  if (hit === undefined) return config;
  // One-shot: drop the entry so subsequent fetches go over the wire.
  delete cache[normalized];
  delete cache[key];
  delete cache["/api" + normalized];
  // Short-circuit axios by returning a rejected config with an adapter that
  // resolves the cached body. Using `config.adapter` is the documented axios
  // hook for this.
  config.adapter = () =>
    Promise.resolve({
      data: hit,
      status: 200,
      statusText: "OK (prerender cache)",
      headers: {},
      config,
      request: null,
    });
  return config;
});

export { API };
export default api;
